const LANGUAGE_ALIASES = {
  en: 'en',
  'en-us': 'en',
  'en_gb': 'en',
  english: 'en',
  uk: 'uk',
  ua: 'uk',
  'uk-ua': 'uk',
  'uk_ua': 'uk',
  ukrainian: 'uk',
  'українська': 'uk'
}

const THEME_ALIASES = {
  dark: 'dark',
  'dark-mode': 'dark',
  night: 'dark',
  midnight: 'dark',
  'theme-dark': 'dark',
  light: 'light',
  'light-mode': 'light',
  day: 'light',
  sunrise: 'light',
  'theme-light': 'light',
  system: undefined
}

const SETTINGS_KEYS = {
  language: ['language', 'lang', 'locale'],
  theme: ['theme', 'themeMode', 'mode']
}

let authCache = null
let authCacheInitialized = false

function pickSetting(settings, keys) {
  for (const key of keys) {
    if (settings && settings[key] !== undefined && settings[key] !== null) {
      return settings[key]
    }
  }
  return undefined
}

function normalizeLanguage(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (!normalized) return undefined
  if (Object.prototype.hasOwnProperty.call(LANGUAGE_ALIASES, normalized)) {
    return LANGUAGE_ALIASES[normalized]
  }
  if (normalized.startsWith('en')) return 'en'
  if (normalized.startsWith('uk') || normalized.startsWith('ua')) return 'uk'
  return undefined
}

function normalizeTheme(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (!normalized) return undefined
  if (Object.prototype.hasOwnProperty.call(THEME_ALIASES, normalized)) {
    return THEME_ALIASES[normalized]
  }
  if (normalized.includes('dark')) return 'dark'
  if (normalized.includes('light')) return 'light'
  return undefined
}

export function normalizeUserSettings(settings = {}) {
  const languageValue = pickSetting(settings, SETTINGS_KEYS.language)
  const themeValue = pickSetting(settings, SETTINGS_KEYS.theme)

  return {
    lang: normalizeLanguage(languageValue),
    themeMode: normalizeTheme(themeValue)
  }
}

export function getNormalizedProfileSettings() {
  const auth = getAuthData()
  if (!auth?.user?.settings) {
    return normalizeUserSettings()
  }
  const normalized = normalizeUserSettings(auth.user.settings)

  if (normalized.lang && auth.user.settings.language !== normalized.lang) {
    auth.user.settings.language = normalized.lang
    try {
      localStorage.setItem('auth', JSON.stringify(auth))
    } catch {
      // ignore persistence errors
    }
  }

  return normalized
}

function readAuthFromStorage() {
  try {
    const raw = localStorage.getItem('auth')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function normalizeAuthPayload(rawAuth) {
  if (!rawAuth || typeof rawAuth !== 'object') {
    return null
  }

  const token = typeof rawAuth.token === 'string' && rawAuth.token.trim()
    ? rawAuth.token.trim()
    : null
  const expiresAt = typeof rawAuth.expiresAt === 'number'
    ? rawAuth.expiresAt
    : Number(rawAuth.expiresAt)

  const normalized = {
    ...rawAuth,
    token,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : null
  }

  if (!normalized.token || !normalized.expiresAt) {
    return null
  }

  return normalized
}

export function setAuthData(auth, {persist = true} = {}) {
  const normalized = normalizeAuthPayload(auth)
  authCache = normalized
  authCacheInitialized = true

  if (!persist || typeof window === 'undefined') {
    return normalized
  }

  try {
    if (normalized) {
      localStorage.setItem('auth', JSON.stringify(normalized))
    } else {
      localStorage.removeItem('auth')
    }
  } catch {
    // ignore persistence errors
  }

  return normalized
}

export function rehydrateAuthData() {
  if (typeof window === 'undefined') {
    authCache = null
    authCacheInitialized = true
    return null
  }

  const normalized = normalizeAuthPayload(readAuthFromStorage())
  if (!normalized) {
    setAuthData(null)
    return null
  }

  if (normalized.expiresAt <= Date.now()) {
    setAuthData(null)
    return null
  }

  authCache = normalized
  authCacheInitialized = true
  return normalized
}

export function getAuthData() {
  if (authCacheInitialized) {
    return authCache
  }

  return rehydrateAuthData()
}

export function isAuthDataHydrated() {
  return authCacheInitialized
}

export function checkAuth() {
  const data = getAuthData()
  return !!data?.token && data.expiresAt > Date.now()
}

export function profileComplete() {
  const data = getAuthData()
  const s = data?.user?.settings || {}
  const currency = s.baseCurrency || s.currency
  return !!(s.country && s.language && currency && s.timezone)
}
